import { useState, useEffect } from "react";
import Card from "../components/Card";
import LayerItem from "./LayerItem";
import ComplianceSummary from "./ComplianceSummary";
import { ListRowPlaceholder } from "../components/ListRow";
import Collapse from "../components/Collapse";
import { ArrowIcon } from "../components/Icons";
import useRequestedDataLayer from "./useRequestedDataLayer";
import useForm from "../hooks/useForm";
import useNavigateZoomToZoneLaPopup from "../hooks/useNavigateZoomToZoneLaPopup";
import { APP_SETTINGS, ARCGIS_SERVICES_ROOT_URL } from "../settings";
import useEsri from "../hooks/useEsri";
import usePollutantMetric from "../pollutant-metric/usePollutantMetric";
import {
  isFieldSelected,
  getQueryLayerUniqueCode,
  getPopupNameByQueryLayer,
} from "../utils";
import { displayRequestInforToast } from "../HelpToaster";

const BACKGROUND_LAYER_OPACITY = 0.7;
const { ZONE_URL, LA_URL } = APP_SETTINGS.GIS_SERVER;

const getRenderOptionWithUniqueId = (
  getSelectedQueryLayer,
  selectedZoneLaUniqueCode
) => {
  const uniqueCodeSlug = getQueryLayerUniqueCode(getSelectedQueryLayer);
  const uniqueCodeWithAlias = uniqueCodeSlug + `'${selectedZoneLaUniqueCode}'`;
  return {
    layerServiceURL: null,
    uniqueCode: uniqueCodeWithAlias,
    layerUniqueId: "",
    layerOptions: null,
  };
};

const InformationCard = ({ position, customClass }) => {
  const [serviceResponse, setServiceResponse] = useState();
  const {
    onPollutantMetricFieldChange,
    onZoneLaListFieldChange,
    onQueryLayerFieldChange,
    getSelectedPollutantMetric,
    getSelectedQueryLayer,
  } = useForm();

  const { selectedZoneLaUniqueCode } = useNavigateZoomToZoneLaPopup({
    onZoneLaListFieldChange,
    onQueryLayerFieldChange,
    getSelectedQueryLayer,
  });

  const {
    loading,
    fixed_measurement_point,
    supplementary_measurement_point,
    roadside,
    background,
    roadside_comp,
    background_comp,

  } = useRequestedDataLayer({
    serviceResponse,
  });

  const { getPollutantMetricItemById } = usePollutantMetric();

  const { geoProcessor, destroyToken, registerToken } = useEsri();

  const doNotMount =
    !isFieldSelected(selectedZoneLaUniqueCode) ||
    !isFieldSelected(onPollutantMetricFieldChange);

  useEffect(() => {
    if (!serviceResponse) {
      return;
    }
    if ((background_comp === null && roadside_comp === null) || isOpen === true) {
      setIsOpen(false)
    }
    displayRequestInforToast();
  }, [serviceResponse]);

  useEffect(() => {
    if (doNotMount) {
      return;
    }

    console.log(
      " %c ============== Requested Information Card ===========",
      "background: #1a2; color: #fff"
    );
    (async () => {
      destroyToken();
      const { GP } = APP_SETTINGS.GIS_SERVER;
      let gpServiceInfo = await geoProcessor(GP.SERVICE_INFO_URL);
      const params = {
        unique_id: selectedZoneLaUniqueCode,
        pollutant_metric_id: onPollutantMetricFieldChange,
      };
      const serviceInfoResponse = await gpServiceInfo.execute(params);
      if (serviceInfoResponse.results[0]["value"]) {
        const gpServiceResponse = serviceInfoResponse.results[0]["value"];
        const { token } = gpServiceResponse;
        const tokenJSON = {
          token: token,
          server: ARCGIS_SERVICES_ROOT_URL,
          ssl: true,
        };
        registerToken(tokenJSON);
        const renderOptions = getRenderOptionWithUniqueId(
          getSelectedQueryLayer,
          selectedZoneLaUniqueCode
        );
        const { name: pollutant_metric_name } = getPollutantMetricItemById(
          getSelectedPollutantMetric
        );
        const gpServiceInfoOptions = {
          token: token,
          unique_id: selectedZoneLaUniqueCode,
        };
        const popupTitle = getPopupNameByQueryLayer(getSelectedQueryLayer);
        const highlightLayerUrl =
          getSelectedQueryLayer === "zone" ? ZONE_URL : LA_URL;

        setServiceResponse({
          gpServiceResponse,
          renderOptions,
          pollutant_metric_name,
          gpServiceInfoOptions,
          highlightLayerUrl,
          popupTitle,
          getSelectedQueryLayer,
        });
      }
    })();

    return () => {
      /* clean up */
    };
  }, [
    destroyToken,
    doNotMount,
    geoProcessor,
    onPollutantMetricFieldChange,
    registerToken,
    selectedZoneLaUniqueCode,
    getSelectedQueryLayer,
    getSelectedPollutantMetric,
    getPollutantMetricItemById,
  ]);

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (background_comp || roadside_comp) {
      setIsOpen(true)
    } else if ((background_comp === null && roadside_comp === null) || isOpen === true) {
      setIsOpen(false)
    }
  }, [background_comp, roadside_comp])

  if (doNotMount) {
    return null;
  }

  const toggle = () => {
    setIsOpen(!isOpen);
  }

  return (
    <>
      <Card
        containerStyle={`${position} ${customClass} xs-information`}
        bodyClassNames="px-3 py-0"
        testId="information"
      >
        <div>
          <div className="w-4/5">
            <h3 className="xs-information-text font-bold text-xl w-64 mt-2">
              Requested Information
            </h3>
          </div>
        </div>
        <ComplianceSummary uniqueCode={selectedZoneLaUniqueCode} />

        {/* begin layer list */}
        {
          (background_comp || roadside_comp) && (
            <>
              <div
                role="button"
                className="flex flex-wrap cursor-pointer"
                onClick={toggle}
              >
                <div className="w-4/5">
                  <h3 className="xs-information-text font-bold text-xl w-64 mt-2">
                    Compliance data
                  </h3>
                </div>
                <div className="w-1/5">
                  <ArrowIcon />
                </div>
              </div>
              <Collapse isOpen={isOpen}>
                {!loading ? (
                  <div
                    id="primarylayers"
                    className={`mt-3 ${isOpen ? `animate-fade-in-down` : ``}`}
                  >
                    <div id="primaryMeasurementsLayers">
                      {background_comp && (
                        <LayerItem
                          name="Compliance Background"
                          layerRefId="polygons_comp"
                          checked={background_comp && isOpen}
                          gpInfo={background_comp}
                          defaultOpacity={BACKGROUND_LAYER_OPACITY}
                        />
                      )}

                      {roadside_comp && (
                        <LayerItem
                          name="Compliance Roadside"
                          layerRefId="roadside_comp"
                          checked={roadside_comp && isOpen}
                          gpInfo={roadside_comp}
                        />
                      )}
                    </div>
                  </div>
                  /* end layer list */
                ) : (
                  <>
                    <ListRowPlaceholder />
                    <ListRowPlaceholder />
                  </>
                )}
              </Collapse>
            </>
          )
        }
        {/* end layer list */}

        {/* begin layer list */}
        <div
          role="button"
          className="flex flex-wrap cursor-pointer"
          onClick={toggle}
        >
          <div className="w-4/5">
            <h3 className="xs-information-text font-bold text-xl w-64 mt-2">
              All data
            </h3>
          </div>
          <div className="w-1/5">
            <ArrowIcon />
          </div>
        </div>
        <Collapse isOpen={!isOpen}>
          {!loading ? (
            <div
              id="otherMeasurementlayers"
              className={`mt-3 ${!isOpen ? `animate-fade-in-down` : ``}`}
            >
              <div id="otherMeasurementlayers" hidden={false}>
                {fixed_measurement_point && (
                  <LayerItem
                    name="Fixed Measurements"
                    layerRefId="fixed_measurement_point"
                    checked={!(background_comp || roadside_comp) ? fixed_measurement_point : fixed_measurement_point && !isOpen}
                    gpInfo={fixed_measurement_point}
                  />
                )}
                {supplementary_measurement_point && (
                  <LayerItem
                    name="Supplementary Measurements"
                    layerRefId="supplementary_measurement_point"
                    checked={!(background_comp || roadside_comp) ? supplementary_measurement_point : supplementary_measurement_point && !isOpen}
                    gpInfo={supplementary_measurement_point}
                  />
                )}
                {roadside && (
                  <LayerItem
                    name="Modelled Roadside"
                    layerRefId="roadside"
                    gpInfo={roadside}
                    checked={!(background_comp || roadside_comp) ? roadside : roadside && !isOpen}
                  />
                )}
                {background && (
                  <LayerItem
                    name="Modelled Background"
                    layerRefId="polygons"
                    gpInfo={background}
                    checked={!(background_comp || roadside_comp) ? background : background && !isOpen}
                    defaultOpacity={BACKGROUND_LAYER_OPACITY}
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              <ListRowPlaceholder />
              <ListRowPlaceholder />
            </>
          )}
        </Collapse>
        {/* end layer list */}
      </Card>
    </>
  );
};

export default InformationCard;
